<script>
  // eslint-disable-next-line no-unused-vars
  import * as Types from "./newsLetterTypes";

  // @ts-check
  import { createEventDispatcher } from "svelte";

  import Link from "../../atoms/Link.svelte";
  import FormField from "../../molecules/FormFields/FormField.svelte";

  /** Primarily used for testing
   * @type {string} */
  export let id = undefined;

  /**
   * optional prop for handling form submission natively
   *
   * @type {Types.FormProps}
   * */
  export let formProps = {};

  /**
   * @type {*} props - The component props.
   */
  export let formFields = [];

  /** Indicates whether the form is currently being submitted.
   * @type {boolean} */
  export let isSubmitting = undefined;

  const dispatch = createEventDispatcher();

  /** Indicates that the input is valid after form is pre-submmited (submit clicked).
   * */
  let validityState = {};

  /** The text to display on the submit error.
   * @type {string} */
  export let formSubmitErrorMessage;

  $: ariaLiveTextOnSubmitting = isSubmitting
    ? "sending"
    : formSubmitErrorMessage
      ? formSubmitErrorMessage
      : "";
  let ariaLiveTextOnValidatingFields;

  const submitButton = formFields.find((f) => f.type === "submit");

  /** The form submit event handler.
   * @param {SubmitEvent} event - The form submit event.
   */
  const onSubmit = (event) => {
    if (formProps.action && formProps.method) {
      return;
    }
    event.preventDefault();
    const formData = new FormData(event.target);
    dispatch("formSubmit", Object.fromEntries(formData));
  };

  const handleInputValidity = (event) => {
    const { id } = event.detail;
    validityState = { ...validityState, [id]: false };
    ariaLiveTextOnValidatingFields += `${id} is not valid.`;
  };

  const handleFormClick = () => {
    validityState = {};
    ariaLiveTextOnValidatingFields = ""; // Reset the aria live text
  };
</script>

<form {id} {...formProps} on:submit={onSubmit} class="flex flex-col gap-5">
  {#each formFields.filter((f) => f.type !== "submit") as formField (formField?.id)}
    <FormField
      id={formField?.id || `${id}-${formField?.label}`}
      name={formField?.name}
      type={formField?.type}
      label={formField?.label}
      value={formField?.value}
      required={formField?.required}
      errorMessage={formField?.errorMessage}
      isValidValue={validityState[formField?.id]}
      on:invalid={handleInputValidity}
      componentClass={`mb-3 h-auto`}
    >
      <svelte:fragment>
        {#if formField?.type === "checkbox"}
          <span>
            Yes, I agree to the
            <Link
              variant="inline"
              href="/terms-of-service"
              class="underline"
              label="Terms of Service"
            />
          </span>
        {/if}
      </svelte:fragment>
    </FormField>
  {/each}

  <div class="relative pb-7 lg:pb-10">
    <FormField
      id={submitButton?.id || `${id}-submit`}
      type="submit"
      label={submitButton?.label || "Submit"}
      variant={isSubmitting ? "filled" : "outline"}
      isLoading={isSubmitting}
      theme="white"
      buttonClass="mt-10 lg:mt-4"
      on:click={handleFormClick}
    />
    {#if formSubmitErrorMessage}
      <div class="label absolute bottom-0 text-system-red">
        {formSubmitErrorMessage}
      </div>
    {/if}
  </div>

  <div aria-live="assertive" class="sr-only">
    {ariaLiveTextOnValidatingFields ? ariaLiveTextOnValidatingFields : ariaLiveTextOnSubmitting}
  </div>
</form>
